export const locales = ["en", "ro"] as const;

export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = "en";

export const localeLabels: Record<Locale, string> = {
  en: "EN",
  ro: "RO",
};

export const localeNames: Record<Locale, string> = {
  en: "English",
  ro: "Română",
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && locales.includes(value as Locale);
}

export function parseLocale(value: unknown): Locale {
  return isLocale(value) ? value : defaultLocale;
}

/** Strip a leading locale segment from a pathname (`/ro/about/` → `/about/`). */
export function stripLocaleFromPathname(pathname: string): string {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const parts = normalized.split("/");
  // ["", "ro", "about", ""] or ["", "en", ""]
  if (parts.length >= 2 && isLocale(parts[1])) {
    const rest = parts.slice(2).join("/");
    return rest ? `/${rest}` : "/";
  }
  return normalized || "/";
}

/**
 * Prefix an internal href with the active locale.
 * Leaves external, mailto, tel, and hash-only links untouched.
 */
export function localizedHref(locale: Locale, href: string): string {
  if (!href) return `/${locale}/`;
  if (
    href.startsWith("http://") ||
    href.startsWith("https://") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:") ||
    href.startsWith("//")
  ) {
    return href;
  }

  if (href.startsWith("#")) {
    return `/${locale}/${href}`;
  }

  // "/#section" style
  if (href.startsWith("/#")) {
    return `/${locale}/${href.slice(1)}`;
  }

  const hashIndex = href.indexOf("#");
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : "";
  const pathOnly = hashIndex >= 0 ? href.slice(0, hashIndex) : href;

  const bare = stripLocaleFromPathname(pathOnly);
  if (bare === "/") return `/${locale}/${hash}`;

  const withSlash = bare.endsWith("/") ? bare : `${bare}/`;
  return `/${locale}${withSlash}${hash}`;
}

/** Build the same page in another locale. */
export function switchLocalePath(pathname: string, nextLocale: Locale): string {
  const bare = stripLocaleFromPathname(pathname);
  return localizedHref(nextLocale, bare);
}
